// Client-tool channel executor. The backend agent emits a
// client_tool_request SSE event when it needs live workbook data; we run
// the operation via Office.js and POST the result (or the failure
// reason) back so the parked agent iteration can continue. Errors are
// NEVER swallowed here — an {ok:false, error} result is how the model
// learns why an operation failed and self-corrects.

import { api } from './api';
import { captureWorkbookSnapshot, readRangeLive } from './office';
import type { ClientToolRequestPayload } from '@/types/chat';

interface ClientToolResultBody {
  request_id: string;
  ok: boolean;
  result?: unknown;
  error?: string;
}

async function postResult(body: ClientToolResultBody): Promise<void> {
  try {
    await api.post('/api/v1/chat/client-tool-result', body);
  } catch (err) {
    // 404 = the server already timed out this request; 409 = duplicate.
    // Neither is recoverable from here — log and move on. The backend
    // degrades the read to an explicit tool error for the model.
    console.warn('[clientTools] result delivery failed', body.request_id, err);
  }
}

/**
 * Executes one client-tool request and reports back. Designed to be
 * fired WITHOUT await from the SSE onmessage handler — it must never
 * block stream parsing.
 */
export async function executeClientToolRequest(req: ClientToolRequestPayload): Promise<void> {
  try {
    switch (req.op) {
      case 'read_range': {
        const rangeA1 = req.params?.range?.trim();
        if (!rangeA1) {
          await postResult({ request_id: req.request_id, ok: false, error: 'missing range parameter' });
          return;
        }
        const result = await readRangeLive(req.params?.sheet, rangeA1);
        await postResult({ request_id: req.request_id, ok: true, result });
        return;
      }
      case 'capture_snapshot': {
        // Fresh capture on purpose — the whole point of this op is to
        // bypass the cached snapshot the turn started with.
        const snapshot = await captureWorkbookSnapshot();
        await postResult({ request_id: req.request_id, ok: true, result: snapshot });
        return;
      }
      default:
        await postResult({
          request_id: req.request_id,
          ok: false,
          error: `unsupported client tool op: ${String(req.op)}`,
        });
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    await postResult({ request_id: req.request_id, ok: false, error: message });
  }
}
